import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { Observable, Subscription, interval, filter } from 'rxjs';

import { AdminPreferencesService } from '../../../core/services/admin-preferences.service';
import { AuthService } from '../../../core/services/auth.service';
import { ShopService } from '../../../core/services/shop.service';
import { LicenseService } from '../../../core/services/license.service';
import { BankService } from '../../../core/services/bank.service';
import { api } from '../../../core/config/api.config';
import { UserEntity } from '../../../core/models/user.model';
import { clientHasStorefrontAccess } from '../../../core/guards/storefront-entitlement.guard';

import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { crispLoader } from '../../../core/utils/support-chat-loader';

import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { GlobalStateService } from '../../../core/services/global-state.service';
import { DashboardNotificationsService } from '../../../core/services/dashboard-notifications.service';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [RouterModule, MatIconModule, MatRippleModule, MatButtonModule, MatMenuModule, TranslateModule, NotificationBellComponent],
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
    private router = inject(Router);
    private authService = inject(AuthService);
    private shopService = inject(ShopService);
    private licenseService = inject(LicenseService);
    private bankService = inject(BankService);
    private breakpointObserver = inject(BreakpointObserver);
    private translate = inject(TranslateService);
    private globalState = inject(GlobalStateService);
    private dashNotifications = inject(DashboardNotificationsService);
    private adminPreferences = inject(AdminPreferencesService);

    user: UserEntity | null = null;
    client: any;
    clientKey = '';
    isVisible = true;
    badges: any;
    sidebarVisible = false; // Start hidden on mobile
    sidebarHeight = 800;
    intervalTimer: Subscription | undefined;
    authSubscription: Subscription | undefined;
    breakpointSubscription: Subscription | undefined;
    service_url: string | null = null;
    showMarketing = false;
    clientAccountUrl = '';
    isActiveSaasClient = false;
    showInventory = false;
    showKitchen = false;
    isMobile = false;
    isTablet = false;
    isDesktop = true;

    // Site Selector Data
    sites: any[] = [];
    selectedSite: any = null;

    // CDN base for media URLs. The Media model returns relative paths
    // only ("frontends add CDN prefix"); the sidebar logo <img> binds
    // to `cdnUrl + '/' + selectedSite.logo.url`.
    cdnUrl: string = api.url_cdn;

    // Footer Data
    currentYear = new Date().getFullYear();
    currentLang = 'en';

    setLanguage(lang: string) {
        this.globalState.setLanguage(lang);
        this.translate.use(lang);
        this.currentLang = lang;
        // LocalStorage is handled by AppComponent subscription, but we can sync here too if needed
        this.authService.emitChange();

        // Mirror the choice onto the admin's server-side preference so their
        // email arrives in the language they read the dashboard in. Without
        // this, admins.language has no way of ever being set: admins have no
        // CRUD surface, so every admin would sit on the English fallback and
        // Dutch admin mail would be unreachable.
        //
        // Admin-only: a merchant's language lives on clients.locale and is not
        // this endpoint's business. Errors are swallowed on purpose — the UI
        // language has already switched, and failing to persist a preference
        // must not surface as an error over a successful interaction.
        if (this.authService.isAdmin()) {
            this.adminPreferences.setLanguage(lang).subscribe({
                error: () => { /* preference is best-effort; UI already updated */ }
            });
        }
    }

    onSiteChange(site: any) {
        this.selectedSite = site;
        this.globalState.setSite(site);
        // Navigate or refresh state if needed
        this.getBadges();
        // Optional: Trigger a router navigation to same URL to refresh resolves if necessary
        // this.router.navigate([], { queryParams: { suid: site.uniq }, queryParamsHandling: 'merge' });
    }

    loadSites() {
        if (this.isConnectClient() || (this.user?._role === 'ADMIN' && !this.client)) {
            this.shopService.getSites().subscribe((sites) => {
                this.sites = sites;

                // Initialize selected site from Global State or Service URL default
                const currentGlobalSite = this.globalState.getCurrentSite();
                if (currentGlobalSite) {
                    // Check if valid
                    const match = this.sites.find(s => s.uniq === currentGlobalSite.uniq);
                    if (match) {
                        this.selectedSite = match;
                    } else if (this.sites.length > 0) {
                        this.onSiteChange(this.sites[0]);
                    } else {
                        // No sites for the current context (e.g. just
                        // disconnected from a client): drop the stale
                        // selection so the sidebar falls back to the
                        // Vlugbezorgd logo instead of the previous client's.
                        this.selectedSite = null;
                    }
                } else if (this.sites.length > 0) {
                    this.onSiteChange(this.sites[0]);
                } else {
                    this.selectedSite = null;
                }
            });
        }
    }

    checkInventoryAccess() {
        if (this.client && this.client.sites && this.client.sites.length > 0) {
            this.showInventory = this.client.sites.some((s: any) => {
                const shopType = s.shop_type ? s.shop_type.toLowerCase() : '';
                const hasRealStock = s.real_stock === true || s.real_stock === "1" || s.real_stock === 1 || s.real_stock === 'true';
                return shopType === 'retail' || hasRealStock;
            });
            this.showKitchen = this.client.sites.some((s: any) =>
                (s.shop_type ? s.shop_type.toLowerCase() : '') === 'restaurant'
            );
        } else {
            this.showInventory = false;
            this.showKitchen = false;
        }
    }

    private visibilityHandler?: () => void;

    constructor() {
        this.user = this.authService.getUser();
        this.client = this.authService.getClient();
        this.service_url =
            this.client && this.client.sites && this.client.sites[0] ? this.client.sites[0].service_url : null;
        this.checkInventoryAccess();

        // Initialize sidebar visibility based on window size
        // Desktop (1024px+) should show sidebar by default
        if (typeof window !== 'undefined') {
            this.sidebarVisible = window.innerWidth >= 1024;

            // Set initial language from service or local storage
            const savedLang = localStorage.getItem('vlugbezorgd_lang');
            this.currentLang = savedLang || this.translate.currentLang || this.translate.defaultLang || 'en';
        }
    }

    ngOnInit() {
        if (this.client && this.client.uniq) {
            this.getApiKey();
        }

        // Responsive breakpoint detection
        this.breakpointSubscription = this.breakpointObserver
            .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
            .subscribe(result => {
                this.isMobile = result.breakpoints[Breakpoints.XSmall] || result.breakpoints[Breakpoints.Small];
                this.isTablet = result.breakpoints[Breakpoints.Medium];
                this.isDesktop = result.breakpoints[Breakpoints.Large] || result.breakpoints[Breakpoints.XLarge];

                // Auto-show sidebar on desktop, hide on mobile/tablet
                if (this.isDesktop) {
                    this.sidebarVisible = true;
                } else {
                    this.sidebarVisible = false;
                }
            });

        // Close sidebar on navigation for mobile/tablet
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                if (!this.isDesktop) {
                    this.sidebarVisible = false;
                }
            });

        if (this.user) {
            if (this.client && this.client.uniq) {
                const getStripeObs = (this.user._role === 'ADMIN')
                    ? this.bankService.getClientStripeProvider(this.client.uniq)
                    : this.bankService.getStripeExternalProvider();

                getStripeObs.subscribe({
                    next: async (res: any) => {
                        if (
                            res &&
                            res.data &&
                            res.data.client &&
                            res.data.client.external_providers &&
                            res.data.client.external_providers.length
                        ) {
                            const { api_key } = res.data.client.external_providers[0];

                            if (api_key) {
                                this.isActiveSaasClient = true;
                            }
                        }
                    },
                    error: () => {
                        // Ignore provider loading errors on background checks
                    }
                });
            }
            this.clientAccountUrl = api.admin_v3_url;
        }

        this.loadSites();

        this.getBadges();
        this.haveAccessToMarketing();
        this.loadSupportChat();
        this.dashNotifications.init();

        this.authSubscription = this.authService.changeEmitted$.subscribe(() => {
            this.refreshState();
        });

        // Force refresh client data from backend to ensure real_stock status is up to date
        const user = this.authService.getUser();
        if (user && user._token) {
            this.authService.setClient(user._token).subscribe({
                next: () => {
                    this.refreshState();
                },
                error: () => {
                    // Ignore client refresh error on transient network issues
                }
            });
        }

        // Skip polling when page is hidden to reduce load, refresh immediately on return
        if (typeof document !== 'undefined') {
            this.visibilityHandler = () => {
                if (!document.hidden) {
                    this.refreshState();
                }
            };
            document.addEventListener('visibilitychange', this.visibilityHandler);
        }

        this.intervalTimer = interval(60000).subscribe(() => {
            if (typeof document !== 'undefined' && document.hidden) {
                return;
            }
            this.refreshState();
        });
    }

    refreshState() {
        this.user = this.authService.getUser();
        this.client = this.authService.getClient();
        this.service_url =
            this.client && this.client.sites && this.client.sites[0] ? this.client.sites[0].service_url : null;
        this.getBadges();
        this.checkInventoryAccess();
        this.haveAccessToMarketing();
        // Re-fetch sites so selectedSite (and its logo) reflect the client
        // just connected-as/disconnected-from, instead of staying stale
        // until the next full page reload.
        this.loadSites();

        // Sync language from storage if changed elsewhere
        const storedLang = localStorage.getItem('vlugbezorgd_lang');
        if (storedLang && storedLang !== this.currentLang) {
            this.currentLang = storedLang;
            this.translate.use(storedLang);
        }
    }

    loadSupportChat() {
        setTimeout(() => {
            crispLoader(this.licenseService, this.authService);
        }, 100);
    }

    ngOnDestroy() {
        if (this.intervalTimer) this.intervalTimer.unsubscribe();
        if (this.authSubscription) this.authSubscription.unsubscribe();
        if (this.breakpointSubscription) this.breakpointSubscription.unsubscribe();
        if (typeof document !== 'undefined' && this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
        }
    }

    getApiKey() {
        this.clientKey = this.authService.getClientId();
    }

    isConnectClient() {
        if (this.user && (this.user._role === 'CLIENT' || (this.user._role === 'ADMIN' && this.user._token))) {
            return true;
        } else {
            return false;
        }
    }

    // True while an ADMIN has "connected as" a client. The top sidebar logo
    // must stay Vlugbezorgd's own branding in this state — the client's own
    // logo (selectedSite.logo, resolved once loadSites() picks up the
    // impersonated client's sites) belongs in the ADMIN MODE badge instead.
    isAdminImpersonating(): boolean {
        return !!(this.client && this.user?._role === 'ADMIN');
    }

    get clientLogoUrl(): string | null {
        const logo = this.selectedSite?.logo;
        const path = logo?.url || logo?.path;
        return path ? `${this.cdnUrl}/${path}` : null;
    }

    isUs() {
        if (this.user &&
            (this.user._adminUsername === 'radmin' ||
                this.user._adminUsername === 'badmin' ||
                this.user._adminUsername === 'superadmin'
            )) {
            return true;
        } else {
            return false;
        }
    }

    haveAccessToMarketing() {
        this.showMarketing = !!(this.client?.sites || []).some((s: any) => s.license?.marketing);
    }

    hasStorefrontAccess(): boolean {
        // Resolve against the client's licensed sites: the selected site (from /shop/sites)
        // does not carry the license relation, so trusting it directly hides the link even
        // when the Web Storefront is active.
        return clientHasStorefrontAccess(this.client?.sites, this.globalState.getCurrentSite());
    }

    adminDisconectClient() {
        this.authService.removeApiKey();
        this.user = this.authService.getUser();
        this.client = this.authService.getClient();
        // Otherwise selectedSite (and its logo) stay pinned to the client
        // just disconnected from until the next full page reload.
        this.selectedSite = null;
        this.loadSites();
        this.router.navigate(['/admin/clients', '']);
    }

    getBadges() {
        const onResp = (r: any) => {
            if (r) {
                this.badges = r;
            }
        };
        const onError = (err: any) => {
            // Retain last-known badge values (do not reset this.badges).
            // Status 0 represents transient network failures (offline, connection drops, aborts)
            // and must never reach Sentry or the global ErrorHandler.
            if (err?.status !== 0) {
                console.error('Failed to fetch sidebar badges', err);
            }
        };

        // Prefer current global site over local storage
        const currentSite = this.globalState.getCurrentSite();
        const siteUniq = currentSite?.uniq || localStorage.getItem('selectedSiteUniq') || undefined;

        if (this.user && this.user._role === 'ADMIN') {
            this.shopService.getBadgesAdmin(this.client, siteUniq).subscribe({
                next: onResp,
                error: onError
            });
        } else if (this.user && this.user._role === 'CLIENT') {
            this.shopService.getBadgesClient(siteUniq).subscribe({
                next: onResp,
                error: onError
            });
        }
    }

    sidebarToggle() {
        this.sidebarVisible = !this.sidebarVisible;
        this.authService.emitChange();
    }

    logout() {
        this.authService.logout();
        this.router.navigate(['/login']);
    }
}
